import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Wand2, Sparkles, Zap, Download, History, Shield, Check, Play, ArrowRight, Star, Users, Palette, Layers, TrendingUp, Quote, GitBranch, RefreshCw, Lightbulb, ShoppingBag, Store, Briefcase, Edit3, Image, ChevronRight, Gift } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import AuthModal from '@/components/Auth/AuthModal';
import { Helmet } from 'react-helmet';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const LandingNew = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('ASPECT_1_1');

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const aspectRatios = [
    { value: 'ASPECT_1_1', label: 'Square (1:1)' },
    { value: 'ASPECT_16_9', label: 'Wide (16:9)' },
    { value: 'ASPECT_9_16', label: 'Tall (9:16)' },
    { value: 'ASPECT_4_3', label: 'Classic (4:3)' },
    { value: 'ASPECT_3_4', label: 'Portrait (3:4)' },
    { value: 'ASPECT_4_5', label: 'Portrait (4:5)' },
    { value: 'ASPECT_5_4', label: 'Landscape (5:4)' },
  ];

  const features = [
    {
      icon: Wand2,
      title: 'AI-Powered Generation',
      description: 'Transform your ideas into stunning T-shirt designs with cutting-edge AI technology',
      gradient: 'from-cyan-500 to-blue-500',
    },
    {
      icon: Sparkles,
      title: 'Auto Background Removal',
      description: 'Get print-ready PNG files with crystal-clear transparent backgrounds instantly',
      gradient: 'from-turquoise-500 to-teal-500',
    },
    {
      icon: History,
      title: 'Version History',
      description: 'Iterate and experiment freely with complete version control and rollback',
      gradient: 'from-blue-500 to-indigo-500',
    },
    {
      icon: Download,
      title: 'HD Downloads',
      description: 'Export high-resolution images optimized for any print-on-demand platform',
      gradient: 'from-cyan-500 to-turquoise-500',
    },
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Generate professional-grade designs in seconds, not hours or days',
      gradient: 'from-yellow-500 to-orange-500',
    },
    {
      icon: Shield,
      title: 'Secure & Private',
      description: 'Enterprise-grade security keeps your creative assets safe and confidential',
      gradient: 'from-green-500 to-emerald-500',
    },
  ];

  const stats = [
    { number: '50K+', label: 'Designs Created', icon: Palette },
    { number: '10K+', label: 'Happy Creators', icon: Users },
    { number: '99.9%', label: 'Uptime SLA', icon: TrendingUp },
    { number: '4.9/5', label: 'User Rating', icon: Star },
  ];

  const steps = [
    {
      number: '01',
      title: 'Describe Your Vision',
      description: 'Type what you want - our AI understands natural language and creative concepts',
      icon: Wand2,
    },
    {
      number: '02',
      title: 'AI Creates Magic',
      description: 'Watch as advanced AI generates unique, professional designs in seconds',
      icon: Sparkles,
    },
    {
      number: '03',
      title: 'Refine & Perfect',
      description: 'Iterate with AI-powered editing until your design is absolutely perfect',
      icon: Layers,
    },
    {
      number: '04',
      title: 'Download & Launch',
      description: 'Export print-ready files and start selling on your favorite platforms',
      icon: Download,
    },
  ];

  const testimonials = [
    {
      name: 'Sarah Chen',
      role: 'Etsy Shop Owner',
      image: 'https://customer-assets.emergentagent.com/job_creditsmanager/artifacts/fupf9w0r_generation-c6cd58c7-b28f-4ec2-97f4-aa51c1b534fc.png',
      quote: 'Fluxara transformed my business. I can now create designs 10x faster and my sales have tripled!',
      rating: 5,
    },
    {
      name: 'Marcus Rodriguez',
      role: 'Print-on-Demand Creator',
      image: 'https://customer-assets.emergentagent.com/job_creditsmanager/artifacts/eyusimnn_generation-33dba8e0-06c7-4b76-9b72-6b5fc2a6e8e9.png',
      quote: 'The quality is incredible. My customers love the unique designs I create with Fluxara.',
      rating: 5,
    },
    {
      name: 'Emily Watson',
      role: 'Fashion Designer',
      image: 'https://customer-assets.emergentagent.com/job_creditsmanager/artifacts/4un7sfo3_generation-8500ffe6-bebc-44b1-93ed-258228d3059f.png',
      quote: 'As a professional designer, I was skeptical. But Fluxara exceeded all my expectations!',
      rating: 5,
    },
  ];

  const handleGetStarted = () => {
    if (currentUser) {
      navigate('/generate');
    } else {
      setShowAuthModal(true);
    }
  };

  const handleGenerate = () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    // Check if user is logged in
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    if ((userProfile?.credits || 0) < 5) {
      toast.error('Insufficient credits. Please upgrade your plan.');
      return;
    }

    // Navigate to generate page immediately with the prompt and aspect ratio
    navigate('/generate', { 
      state: { 
        prompt, 
        aspectRatio,
        autoGenerate: true 
      } 
    });
  };

  return (
    <>
      <Helmet>
        <title>Fluxara - AI-Powered T-Shirt Design Generator</title>
        <meta
          name="description"
          content="Create stunning T-shirt designs with AI. Transform your ideas into print-ready graphics in seconds."
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50 to-turquoise-50">
        
        {/* Hero Section with Generate Interface */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden px-6 py-20 bg-gradient-to-br from-turquoise-50 via-cyan-50 to-turquoise-100">
          {/* Floating Shapes */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-200/30 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-turquoise-200/30 rounded-full blur-3xl animate-pulse delay-700" />
            <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-blue-200/30 rounded-full blur-3xl animate-pulse delay-1000" />
          </div>

          {/* Hero Content */}
          <div className="relative z-10 max-w-7xl mx-auto text-center w-full">
            <h1 className="text-6xl md:text-8xl font-black mb-16 text-gray-900 leading-tight animate-fade-in-up delay-200">
              Create <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-turquoise-600">AI-Powered</span>
              <br />
              Designs
            </h1>

            {/* Generation Interface */}
            <div className="max-w-3xl mx-auto mt-4 mb-12 animate-fade-in-up delay-400">
              <div className="relative">
                {/* Glow effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 via-turquoise-400 to-yellow-400 rounded-3xl blur-xl opacity-30 animate-pulse" />
                
                <div className="relative flex flex-col md:flex-row md:items-end gap-3 bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-2 pr-3">
                  {/* Text Input - Auto-expanding Textarea */}
                  <Textarea
                    placeholder="e.g., A majestic dragon in cyberpunk style..."
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    rows={1}
                    className="flex-1 px-4 py-3 text-base leading-6 text-gray-900 outline-none bg-transparent border-0 shadow-none focus-visible:ring-0 resize-none min-h-[44px] !max-h-[280px] md:!max-h-[120px] overflow-y-auto placeholder:text-gray-500"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleGenerate();
                      }
                    }}
                    style={{
                      height: 'auto',
                    }}
                    onInput={(e) => {
                      e.target.style.height = 'auto';
                      const maxHeight = window.innerWidth >= 768 ? 120 : 280;
                      e.target.style.height = Math.min(e.target.scrollHeight, maxHeight) + 'px';
                    }}
                  />
                  
                  {/* Controls */}
                  <div className="flex items-center gap-3 w-full md:w-auto self-end">
                    {/* Aspect Ratio */}
                    <Select value={aspectRatio} onValueChange={setAspectRatio}>
                      <SelectTrigger className="flex-[2] md:flex-none md:w-32 h-12 border-0 bg-gray-100 hover:bg-gray-200 text-gray-900 font-medium">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {aspectRatios.map((ratio) => (
                          <SelectItem key={ratio.value} value={ratio.value}>
                            {ratio.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {/* Generate Button */}
                    <Button
                      className="flex-[3] md:flex-none md:px-10 bg-gradient-to-r from-yellow-400 to-cyan-400 hover:from-yellow-300 hover:to-cyan-300 text-gray-900 font-bold h-12 text-lg rounded-full transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                      onClick={handleGenerate}
                    >
                      <Sparkles className="mr-2 w-5 h-5" />
                      Generate
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {!currentUser && (
              <p className="text-gray-700 text-lg mb-16 animate-fade-in-up delay-500">
                <Gift className="inline w-5 h-5 mr-2 text-cyan-600" />
                100 free credits on signup
              </p>
            )}

            {/* Social Proof Bar */}
            <div className={`flex flex-wrap justify-center gap-4 md:gap-6 items-stretch animate-fade-in-up delay-600 ${currentUser ? 'mt-20' : 'mt-0'}`}>
              <div className="text-center bg-white/80 backdrop-blur-sm px-6 md:px-8 py-4 rounded-2xl border border-cyan-200 hover:bg-white/90 transition-all duration-300 min-h-[100px] flex flex-col justify-center w-[calc(50%-8px)] md:w-auto md:min-w-[140px] shadow-lg">
                <p className="text-3xl md:text-4xl font-black text-gray-900 mb-1">50K+</p>
                <p className="text-gray-700 text-xs md:text-sm font-medium">Designs Created</p>
              </div>
              <div className="text-center bg-white/80 backdrop-blur-sm px-6 md:px-8 py-4 rounded-2xl border border-cyan-200 hover:bg-white/90 transition-all duration-300 min-h-[100px] flex flex-col justify-center w-[calc(50%-8px)] md:w-auto md:min-w-[140px] shadow-lg">
                <p className="text-3xl md:text-4xl font-black text-gray-900 mb-1">10K+</p>
                <p className="text-gray-700 text-xs md:text-sm font-medium">Happy Users</p>
              </div>
              <div className="text-center bg-white/80 backdrop-blur-sm px-6 md:px-8 py-4 rounded-2xl border border-cyan-200 hover:bg-white/90 transition-all duration-300 min-h-[100px] flex flex-col justify-center w-[calc(50%-8px)] md:w-auto md:min-w-[140px] shadow-lg">
                <p className="text-3xl md:text-4xl font-black text-gray-900 mb-1">99.9%</p>
                <p className="text-gray-700 text-xs md:text-sm font-medium">Uptime SLA</p>
              </div>
              <div className="text-center bg-white/80 backdrop-blur-sm px-6 md:px-8 py-4 rounded-2xl border border-cyan-200 hover:bg-white/90 transition-all duration-300 min-h-[100px] flex flex-col justify-center w-[calc(50%-8px)] md:w-auto md:min-w-[140px] shadow-lg">
                <div className="flex gap-1 justify-center mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 md:w-5 h-4 md:h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 text-xs md:text-sm font-medium">4.9/5 Rating</p>
              </div>
            </div>
          </div>

          {/* Scroll Indicator - Minimized */}
          <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 opacity-30 hover:opacity-60 transition-opacity">
            <div className="w-5 h-8 border border-white/30 rounded-full flex justify-center">
              <div className="w-0.5 h-2 bg-white/40 rounded-full mt-1.5 animate-pulse" />
            </div>
          </div>
        </section>

        {/* BEFORE & AFTER COMPARISON */}
        <section className="py-32 px-6 bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-cyan-500 to-turquoise-500" />
          </div>
          
          <div className="relative max-w-7xl mx-auto">
            <div className="text-center mb-20">
              <h2 className="text-5xl md:text-6xl font-black mb-6">
                Watch the
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-cyan-400"> Transformation</span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                From simple idea to stunning design in seconds - see the AI magic in action
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Before */}
              <div className="space-y-6">
                <div className="glass-dark p-8 rounded-3xl">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gray-600 rounded-xl flex items-center justify-center">
                      <span className="text-2xl font-black">1</span>
                    </div>
                    <h3 className="text-3xl font-black">Your Idea</h3>
                  </div>
                  <div className="bg-gray-800 p-6 rounded-2xl border-2 border-gray-700">
                    <p className="text-gray-300 text-lg italic">
                      "A majestic dragon breathing colorful flames in cyberpunk style with neon highlights"
                    </p>
                  </div>
                  <div className="mt-6 flex items-center gap-3 text-gray-400">
                    <Edit3 className="w-5 h-5" />
                    <span>Just type what you imagine...</span>
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <div className="hidden md:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="w-20 h-20 bg-gradient-to-r from-yellow-400 to-cyan-400 rounded-full flex items-center justify-center shadow-2xl animate-pulse">
                  <ArrowRight className="w-10 h-10 text-gray-900" />
                </div>
              </div>

              {/* After */}
              <div className="space-y-6">
                <div className="glass-dark p-8 rounded-3xl border-2 border-cyan-400">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-turquoise-400 rounded-xl flex items-center justify-center">
                      <span className="text-2xl font-black">2</span>
                    </div>
                    <h3 className="text-3xl font-black">AI Result</h3>
                  </div>
                  <div className="aspect-square bg-gradient-to-br from-cyan-900 to-turquoise-900 rounded-2xl overflow-hidden border-2 border-cyan-400 relative">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_tshirtai/artifacts/5b1pm8ix_designs_LGOQgurrC7MDV5hZZIVi6lCPNFd2_project_1762792211117_55f29cd7-6021-42b6-89c4-4925dedb924f_original-removebg-preview.png"
                      alt="AI Generated Design"
                      className="w-full h-full object-contain opacity-90"
                    />
                    <div className="absolute top-4 right-4">
                      <div className="bg-yellow-400 text-gray-900 px-4 py-2 rounded-full font-bold text-sm shadow-xl">
                        ✨ AI Generated
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 flex items-center gap-3 text-cyan-300">
                    <Sparkles className="w-5 h-5" />
                    <span>...and watch AI create magic!</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mt-16">
              <p className="text-2xl text-gray-300 mb-8">
                ⏱️ Average generation time: <span className="text-yellow-400 font-bold">30 seconds</span>
              </p>
              <Button
                size="lg"
                className="text-xl px-12 py-8 bg-gradient-to-r from-yellow-400 to-cyan-400 text-gray-900 hover:from-yellow-300 hover:to-cyan-300 shadow-2xl font-bold"
                onClick={handleGetStarted}
              >
                Try It Yourself
              </Button>
            </div>
          </div>
        </section>

        {/* DESIGN GALLERY SHOWCASE */}
        <section className="py-32 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-20">
              <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">
                Real Designs,
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-turquoise-600"> Real Results</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                See what others are creating with Fluxara - from abstract art to bold typography
              </p>
            </div>

            {/* Gallery Grid */}
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {[
                { 
                  image: 'https://customer-assets.emergentagent.com/job_creditsmanager/artifacts/nw3x2sut_generation-e64dcbcc-33fb-4607-b184-981774a342c5.png',
                  prompt: 'Festive holiday design with bells and ornaments',
                  category: 'Holiday'
                },
                {
                  image: 'https://customer-assets.emergentagent.com/job_creditsmanager/artifacts/o9rz1zh4_generation-42e19208-21f0-478c-b093-927c9f7bef76.png',
                  prompt: 'Cute adventure awaits fox character design',
                  category: 'Character'
                },
                {
                  image: 'https://customer-assets.emergentagent.com/job_creditsmanager/artifacts/l36091is_Generated%20Image%20November%2013%2C%202025%20-%2011_16AM.png',
                  prompt: 'Bold pop art style comic expression',
                  category: 'Pop Art'
                },
                {
                  image: 'https://customer-assets.emergentagent.com/job_creditsmanager/artifacts/xtcjgk7e_Generated%20Image%20November%2013%2C%202025%20-%2011_24AM.png',
                  prompt: 'Minimalist botanical inner bloom design',
                  category: 'Minimalist'
                },
                {
                  image: 'https://customer-assets.emergentagent.com/job_creditsmanager/artifacts/ooebqa9m_generation-8e601146-e8f3-4fb5-aca5-f8dbce1588cf.png',
                  prompt: 'Futuristic tech code breaker design',
                  category: 'Tech'
                },
                {
                  image: 'https://customer-assets.emergentagent.com/job_creditsmanager/artifacts/az90wt7w_generation-3d0a5f56-54dd-478d-8053-a31346ed31da.png',
                  prompt: 'Nature-inspired plant lady typography',
                  category: 'Typography'
                },
              ].map((design, index) => (
                <div key={index} className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105">
                  <div className="aspect-square relative">
                    <img 
                      src={design.image}
                      alt={design.category}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <span className="inline-block px-3 py-1 bg-cyan-400 text-gray-900 rounded-full text-sm font-bold mb-3">
                          {design.category}
                        </span>
                        <p className="text-white font-medium italic">"{design.prompt}"</p>
                        <p className="text-cyan-300 text-sm mt-2">Created with Fluxara</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center">
              <Button
                size="lg"
                className="text-xl px-12 py-8 bg-gradient-to-r from-cyan-600 to-turquoise-600 hover:from-cyan-700 hover:to-turquoise-700 shadow-2xl font-bold"
                onClick={handleGetStarted}
              >
                <Palette className="mr-3 w-6 h-6" />
                Create Your Own Design
              </Button>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-32 px-6 bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-cyan-500 to-turquoise-500" />
          </div>
          
          <div className="relative max-w-7xl mx-auto">
            <div className="text-center mb-20">
              <h2 className="text-5xl md:text-6xl font-black mb-6">
                From Idea to Design in
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-cyan-400"> 4 Simple Steps</span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Our intuitive workflow makes professional design accessible to everyone
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={index} className="relative">
                    <div className="flex items-start gap-6">
                      <div className="flex-shrink-0">
                        <div className="relative">
                          <div className="absolute inset-0 bg-cyan-400 rounded-full blur-xl opacity-50" />
                          <div className="relative w-24 h-24 bg-gradient-to-br from-cyan-500 to-turquoise-500 rounded-full flex items-center justify-center text-3xl font-black text-white shadow-2xl">
                            {step.number}
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 pt-4">
                        <div className="flex items-center gap-3 mb-3">
                          <Icon className="w-6 h-6 text-cyan-400" />
                          <h3 className="text-2xl font-bold">{step.title}</h3>
                        </div>
                        <p className="text-gray-300 text-lg leading-relaxed">{step.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="text-center mt-16">
              <Button
                size="lg"
                className="text-xl px-12 py-8 bg-gradient-to-r from-yellow-400 to-orange-400 text-gray-900 hover:from-yellow-300 hover:to-orange-300 shadow-2xl font-bold"
                onClick={handleGetStarted}
              >
                <Play className="mr-3 w-6 h-6" />
                Try It Now - It's Free
              </Button>
            </div>
          </div>
        </section>

        {/* Features Showcase */}
        <section className="py-32 px-6 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-50 to-turquoise-50 opacity-50" />
          <div className="relative max-w-7xl mx-auto">
            <div className="text-center mb-20">
              <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">
                Everything You Need to
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-turquoise-600"> Create Magic</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Powerful AI tools designed to bring your creative vision to life faster than ever before
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Card 
                    key={index} 
                    className="group glass border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105 hover:-translate-y-2"
                  >
                    <CardContent className="p-8">
                      <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${feature.gradient} rounded-2xl mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* VERSION CONTROL & EDITING - THE MOST IMPORTANT FEATURE */}
        <section className="py-32 px-6 bg-gradient-to-br from-cyan-900 via-turquoise-900 to-blue-900 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-20 w-96 h-96 bg-cyan-400 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-turquoise-400 rounded-full blur-3xl animate-pulse delay-1000" />
          </div>
          
          <div className="relative max-w-7xl mx-auto">
            <div className="text-center mb-20">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-400 to-pink-400 rounded-3xl mb-6 shadow-2xl">
                <GitBranch className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-5xl md:text-6xl font-black mb-6">
                Unlimited <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-cyan-300">Iterations</span>
                <br />Perfect Your Design
              </h2>
              <p className="text-xl text-gray-200 max-w-3xl mx-auto">
                Our unique version control system lets you experiment freely and never lose a great design
              </p>
            </div>

            {/* Real Example */}
            <div className="mt-24">

              {/* Horizontal Timeline */}
              <div className="relative">
                {/* Connection Line */}
                <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 transform -translate-y-1/2 z-0" />
                
                <div className="grid lg:grid-cols-3 gap-6 md:gap-8 relative z-10">
                  {/* Version 1 - Original */}
                  <div className="glass-dark p-4 md:p-6 rounded-2xl border-2 border-cyan-400 hover:scale-105 transition-all duration-300">
                    <div className="flex flex-col lg:flex-col-reverse">
                      <div className="mb-3 md:mb-4 lg:mt-3 lg:mb-0">
                        <p className="text-xs md:text-sm text-cyan-300 font-semibold mb-2">ORIGINAL PROMPT:</p>
                        <div className="bg-gray-800/50 p-2 md:p-3 rounded-lg">
                          <p className="text-gray-200 text-xs md:text-sm italic">
                            "A playful line-art illustration of a leaping cat seamlessly integrated into the elegant script text 'Chase Joy,' all in crisp white against a solid black background"
                          </p>
                        </div>
                      </div>

                      <div className="aspect-video md:aspect-square bg-black rounded-xl overflow-hidden mb-3 md:mb-4 flex items-center justify-center p-4">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_creditsmanager/artifacts/pz4x2wrh_a-minimalist-line-art-illustration-featu_GQmf6aH5SpuXxWgaLbBNBQ_uOBrglmrTgyHEJahKBAfBA.png"
                          alt="Chase Joy original design"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Down Arrow 1 (Mobile Only) */}
                  <div className="flex justify-center lg:hidden my-2">
                    <ArrowRight className="w-8 h-8 text-cyan-300 rotate-90" />
                  </div>

                  {/* Version 2 - First Edit */}
                  <div className="glass-dark p-4 md:p-6 rounded-2xl border-2 border-yellow-400 hover:scale-105 transition-all duration-300">
                    <div className="flex flex-col lg:flex-col-reverse">
                      <div className="mb-3 md:mb-4 lg:mt-3 lg:mb-0">
                        <p className="text-xs md:text-sm text-purple-300 font-semibold mb-2">FIRST EDIT:</p>
                        <div className="bg-gray-800/50 p-2 md:p-3 rounded-lg">
                          <p className="text-gray-200 text-xs md:text-sm italic">
                            "Add a small, stylized butterfly just above the cat's outstretched paws for extra whimsy"
                          </p>
                        </div>
                      </div>

                      <div className="aspect-video md:aspect-square bg-black rounded-xl overflow-hidden mb-3 md:mb-4 flex items-center justify-center p-4">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_creditsmanager/artifacts/1mgcuie4_a-minimalist-line-art-illustration-featu__j5Aka9ITnKrdRLl422-Fg_8f79ba_tT-WXdK4AcIohyA.png"
                          alt="Chase Joy with butterfly"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-center gap-2">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full" />
                      <p className="text-xs text-gray-400">Main Version</p>
                    </div>
                  </div>

                  {/* Down Arrow 2 (Mobile Only) */}
                  <div className="flex justify-center lg:hidden my-2">
                    <ArrowRight className="w-8 h-8 text-purple-300 rotate-90" />
                  </div>

                  {/* Version 3 - Second Edit */}
                  <div className="glass-dark p-4 md:p-6 rounded-2xl border-2 border-pink-400 hover:scale-105 transition-all duration-300">
                    <div className="flex flex-col lg:flex-col-reverse">
                      <div className="mb-3 md:mb-4 lg:mt-3 lg:mb-0">
                        <p className="text-xs md:text-sm text-pink-300 font-semibold mb-2">SECOND EDIT:</p>
                        <div className="bg-gray-800/50 p-2 md:p-3 rounded-lg">
                          <p className="text-gray-200 text-xs md:text-sm italic">
                            "Incorporate subtle, sparkling dots trailing behind the butterfly to enhance the sense of movement and magic"
                          </p>
                        </div>
                      </div>

                      <div className="aspect-video md:aspect-square bg-black rounded-xl overflow-hidden mb-3 md:mb-4 flex items-center justify-center p-4">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_creditsmanager/artifacts/vmi0s0kc_a-minimalist-line-art-illustration-featu_nuaZleTRSpiQSwrAiT25Ug_jN-XCbmWR6mooED43Ds7bg.png"
                          alt="Chase Joy with butterfly and sparkles"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Arrows Between Cards (visible on large screens) */}
                <div className="hidden lg:flex absolute top-1/2 left-0 right-0 transform -translate-y-1/2 justify-between px-[30%] pointer-events-none">
                  <ArrowRight className="w-8 h-8 text-cyan-300" />
                  <ArrowRight className="w-8 h-8 text-purple-300" />
                </div>
              </div>
            </div>

            {/* Benefits Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20">
              {[
                { icon: Shield, text: 'Never Lose Work' },
                { icon: RefreshCw, text: 'Easy Rollback' },
                { icon: GitBranch, text: '10 Versions' },
                { icon: Star, text: 'Pick Best Design' },
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="glass-dark p-6 rounded-2xl text-center hover:scale-105 transition-transform duration-300">
                    <Icon className="w-12 h-12 text-cyan-300 mx-auto mb-4" />
                    <p className="font-bold text-lg">{item.text}</p>
                  </div>
                );
              })}
            </div>

            {/* Pro Tip */}
            <div className="mt-16 max-w-4xl mx-auto">
              <div className="p-8 bg-gradient-to-r from-yellow-400/20 to-orange-400/20 rounded-2xl border border-yellow-400/30">
                <div className="flex items-center gap-3 mb-3">
                  <Lightbulb className="w-8 h-8 text-yellow-300" />
                  <p className="font-bold text-2xl">Pro Tip:</p>
                </div>
                <p className="text-gray-200 text-lg">
                  Use version control to create design variations for different audiences or test color schemes before committing!
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* TRUST BADGES & SOCIAL PROOF */}
        <section className="py-20 px-6 bg-white border-y-2 border-gray-200">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-gray-600 font-semibold text-sm uppercase tracking-wider">Trusted By Creators Worldwide</p>
            </div>
            
            <div className="grid grid-cols-3 md:grid-cols-6 gap-8 items-center opacity-60">
              {/* Trust Badge Icons */}
              {[
                { icon: Shield, text: 'Secure' },
                { icon: Users, text: '10K+ Users' },
                { icon: Star, text: '4.9★ Rating' },
                { icon: Zap, text: 'Fast AI' },
                { icon: Download, text: 'HD Quality' },
                { icon: History, text: 'Version Control' },
              ].map((badge, index) => {
                const Icon = badge.icon;
                return (
                  <div key={index} className="text-center group hover:opacity-100 transition-opacity">
                    <Icon className="w-10 h-10 text-cyan-600 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                    <p className="text-sm font-bold text-gray-700">{badge.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* T-SHIRT MOCKUP SHOWCASE */}
        <section className="py-32 px-6 bg-gradient-to-br from-cyan-600 via-turquoise-600 to-blue-600 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl" />
            <div className="absolute bottom-10 left-10 w-96 h-96 bg-yellow-400 rounded-full blur-3xl" />
          </div>
          
          <div className="relative max-w-7xl mx-auto">
            <div className="text-center mb-20">
              <h2 className="text-5xl md:text-6xl font-black mb-6">
                See Your Designs
                <br />
                <span className="text-yellow-300">Come to Life</span>
              </h2>
              <p className="text-2xl text-cyan-100 max-w-3xl mx-auto">
                Visualize your AI-generated designs on actual products before printing
              </p>
            </div>

            {/* Mockup Grid */}
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  image: 'https://customer-assets.emergentagent.com/job_creditsmanager/artifacts/9qfdxsgv_Generated%20Image%20November%2013%2C%202025%20-%2011_27AM.png',
                  title: 'Bloom Where You Planted',
                },
                {
                  image: 'https://customer-assets.emergentagent.com/job_creditsmanager/artifacts/eibgfrcd_Generated%20Image%20November%2013%2C%202025%20-%2011_07AM.png',
                  title: 'Dream Big Little One',
                },
                {
                  image: 'https://customer-assets.emergentagent.com/job_creditsmanager/artifacts/486uvpx8_Generated%20Image%20November%2013%2C%202025%20-%2010_50AM.png',
                  title: 'Chase the Impossible',
                },
              ].map((mockup, index) => (
                <div key={index} className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 to-pink-400 rounded-3xl blur opacity-25 group-hover:opacity-75 transition duration-500" />
                  <div className="relative glass-dark p-4 rounded-3xl">
                    <div className="aspect-square rounded-2xl overflow-hidden">
                      <img 
                        src={mockup.image}
                        alt={mockup.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <p className="text-center font-bold text-xl mt-4">{mockup.title}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-16">
              <h2 className="text-5xl md:text-6xl font-black mb-6">
                Ready to Create Amazing Designs?
              </h2>
              <p className="text-2xl mb-12 text-cyan-100">
                Join thousands of creators and start designing today. No credit card required.
              </p>
              <Button
                size="lg"
                className="text-xl px-16 py-8 bg-white text-cyan-900 hover:bg-gray-100 shadow-2xl font-bold hover:scale-105 transition-transform duration-300"
                onClick={handleGetStarted}
              >
                <Sparkles className="mr-3 w-6 h-6" />
                {currentUser ? 'Start Creating' : 'Start Creating for Free'}
              </Button>
              <p className="mt-8 text-cyan-100 text-lg">
                100 free credits • Cancel anytime • No credit card required
              </p>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-32 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-20">
              <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">
                Loved by
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-turquoise-600"> Creators Worldwide</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Join thousands of designers, entrepreneurs, and artists creating amazing designs
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <Card key={index} className="glass border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                  <CardContent className="p-8">
                    <div className="flex gap-1 mb-6">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <Quote className="w-10 h-10 text-cyan-400 mb-4 opacity-50" />
                    <p className="text-gray-700 text-lg mb-6 leading-relaxed italic">
                      "{testimonial.quote}"
                    </p>
                    <div className="flex items-center gap-4">
                      <img 
                        src={testimonial.image} 
                        alt={testimonial.name}
                        className="w-14 h-14 rounded-full object-cover border-2 border-cyan-400"
                      />
                      <div>
                        <p className="font-bold text-gray-900">{testimonial.name}</p>
                        <p className="text-gray-600 text-sm">{testimonial.role}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* Auth Modal */}
      <AuthModal 
        open={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {
          setShowAuthModal(false);
          navigate('/generate');
        }}
      />
    </>
  );
};

export default LandingNew;
